import { motion } from "framer-motion";
import { CheckCircle2, Zap, Target, UserCheck, Radio } from "lucide-react";

const REP_LEFT = "/rep-you.png";
const REP_RIGHT = "/rep-jordan.png";

const MatchRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3">
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
        accent ? "bg-[#E52525]/15 text-[#E52525]" : "bg-white/5 text-zinc-400"
      }`}
    >
      <Icon size={13} />
    </span>
    <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
      {label}
    </span>
    <span className="ml-auto truncate text-[12px] font-semibold text-zinc-100">
      {value}
    </span>
  </div>
);

export const ProductPreview = () => {
  return (
    <div className="relative">
      {/* Cinematic red glow */}
      <div
        aria-hidden="true"
        className="absolute -inset-10 bg-[radial-gradient(circle_at_60%_40%,rgba(229,37,37,0.18),transparent_55%)] blur-2xl"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-3xl border border-white/10 bg-[#0E0E0E] p-3 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
        data-testid="product-preview"
      >
        {/* Window chrome */}
        <div className="flex items-center justify-between px-2 pb-3 pt-1">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          </div>
          <span className="text-[10px] font-medium tracking-wide text-zinc-600">
            04:12
          </span>
        </div>

        {/* Split screen video */}
        <div className="grid grid-cols-2 gap-3">
          {[REP_LEFT, REP_RIGHT].map((src, idx) => (
            <div
              key={idx}
              className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-zinc-900"
            >
              <img
                src={src}
                alt="Sales rep on a live mock call"
                loading="lazy"
                className="h-full w-full object-cover object-[50%_28%]"
              />
            </div>
          ))}
        </div>

        {/* Live match panel — mirrors the after-call visual footprint but sells
             the core promise: an instant match with another rep. */}
        <div className="mt-4 rounded-2xl border border-[#E52525]/25 bg-[#161616] p-6 shadow-[0_0_40px_-12px_rgba(229,37,37,0.25)]">
          <div className="mb-5 flex items-center justify-between">
            <span className="flex items-center gap-2.5 text-sm font-bold uppercase tracking-[0.15em] text-white">
              <Radio size={18} className="text-[#E52525]" /> Live Match
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-400">
              <span className="sp-live-dot h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
              Connected
            </span>
          </div>
          <div className="flex items-center gap-5">
            <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-black/30 px-5 py-3">
              <span className="font-heading text-3xl font-black leading-none text-white">
                04:12
              </span>
              <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                On call
              </span>
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <MatchRow icon={Zap} label="Matched in" value="8s" accent />
              <MatchRow icon={UserCheck} label="Roles" value="Rep × Prospect" />
              <MatchRow icon={Target} label="Scenario" value="Cold outbound" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating match chip */}
      <motion.div
        animate={{ y: [-6, 6, -6] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-3 top-24 hidden sm:flex items-center gap-2 rounded-2xl border border-white/10 bg-black/60 px-4 py-3 shadow-2xl backdrop-blur-xl"
      >
        <CheckCircle2 size={18} className="text-[#22C55E]" />
        <div className="leading-tight">
          <p className="text-xs font-semibold text-white">You're matched</p>
          <p className="text-[10px] text-zinc-400">Another rep just joined</p>
        </div>
      </motion.div>
    </div>
  );
};
